import { useMutation } from "@apollo/client";
import { useEffect } from "react";
import { useNotify } from "react-admin";
import { useNavigate } from "react-router-dom";

import {
  CompletePlacetelPhoneSignInDocument,
  PlacetelPhoneAccountDocument,
  PlacetelPhoneSignInError,
} from "../../generated/graphql";
import { captureException } from "../../lib/errorHandling";
import { takePhoneSignInCallback } from "../../lib/placetelPhoneSignIn";

/** Finishes the one-time phone sign-in when the browser comes back from it. */
export const PhoneSignInCallback = () => {
  const notify = useNotify();
  const navigate = useNavigate();
  const [completePhoneSignIn] = useMutation(CompletePlacetelPhoneSignInDocument);

  // No "active" guard: React StrictMode runs effects twice and the first run owns the URL.
  useEffect(() => {
    const callback = takePhoneSignInCallback();
    if (callback === null) return;
    const { query, returnPath } = callback;
    navigate(returnPath, { replace: true });

    const complete = async () => {
      try {
        const result = await completePhoneSignIn({
          variables: { input: { query } },
          refetchQueries: [PlacetelPhoneAccountDocument],
        });
        const { errorCode, account } = result.data?.completePlacetelPhoneSignIn ?? {};
        if (errorCode === PlacetelPhoneSignInError.RequestExpired && account?.connected) {
          // Already connected, e.g. the browser went back to a sign-in that was used: nothing to report.
          return;
        }
        if (!errorCode) {
          notify("telephony.activated", { type: "success" });
        } else if (errorCode === PlacetelPhoneSignInError.Cancelled) {
          notify(`telephony.signInErrors.${errorCode}`, { type: "warning" });
        } else {
          notify(`telephony.signInErrors.${errorCode}`, { type: "error" });
        }
      } catch (error) {
        captureException(error, "telephony/phone-sign-in: complete");
        notify("telephony.activateError", { type: "error" });
      }
    };
    void complete();
  }, [completePhoneSignIn, navigate, notify]);

  return null;
};
